import {
  getInstance,
  getCleanInstance,
} from '@portal/repositories/securitySdkConfig';
import { FileTypePayable, TransactionType } from '@portal/models/enumerators';
import * as StorageService from '~/services/storage';
import { API_URL } from '@portal/config/env';

const RECEIVABLE_BASE_URL = '/receivable-group';

export type CreateManualReceivable = {
  amount: number;
  type: TransactionType;
  name: string;
  taxIdentifier: string;
  pixInfo?: {
    key: string;
    charge: {
      dueDate?: string;
      address: {
        zipCode: string;
        country: string;
        state: string;
        city: string;
        street: string;
        neighborhood?: string;
      };
    };
  };
  billetInfo?: {
    discount?: number | null;
    dueDate: string;
    instructions: string;
    interest?: number | null;
    fine?: number | null;
  };
};

export type PaymentLink = {
  amount: number;
  description: string;
  dueDate: string;
  payerName: string;
  payerTaxIdentifier: string;
  payerAddress?: models.Address;
  pixKey: string;
};

const ReceivableApi = {
  getReceivableGroups: async (params: advancedFilterModels.baseFilter) => {
    const instance = getInstance();
    const { data } = await instance.get(RECEIVABLE_BASE_URL, { params });

    return data;
  },

  getReceivables: async (
    id: string,
    params: advancedFilterModels.PaymentsFileAdvancedFilter
  ) => {
    const instance = getInstance();
    const { data } = await instance.get('/receivables/get-all', {
      params: {
        ...params,
        groupId: id,
      },
    });

    return data;
  },

  getReceivablesGroupById: async (id: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${RECEIVABLE_BASE_URL}/${id}`);

    return data;
  },

  getUrl: async (params: { fileExtension: string }) => {
    const instance = getInstance();
    const { data } = await instance.get(`${RECEIVABLE_BASE_URL}/upload-url`, {
      params,
    });

    return data;
  },

  getPreview: async (params: {
    bucket: string;
    name: string;
    fileType?: number;
  }) => {
    const instance = getInstance();
    const { data } = await instance.post(
      `${RECEIVABLE_BASE_URL}/file-preview`,
      {
        ...params,
        fileType: params.fileType || FileTypePayable.CSV,
      }
    );

    return data;
  },

  uploadFile: async (
    fileBody: File,
    fileExtension: string,
    urlToUpload: string
  ) => {
    const instance = getCleanInstance(urlToUpload);
    const { data } = await instance.put('', fileBody, {
      headers: {
        'Content-Type': `file/${fileExtension}`,
      },
    });

    return data;
  },

  createFile: async (params: {
    bucket: string;
    name: string;
    fileType?: number;
    authenticate: {
      pin: string;
    };
  }) => {
    const instance = getInstance();
    const { data } = await instance.post(`${RECEIVABLE_BASE_URL}/file-create`, {
      ...params,
      fileType: params.fileType ?? FileTypePayable.CSV,
    });

    return data;
  },

  createManualReceivable: async (params: CreateManualReceivable) => {
    const instance = getCleanInstance(API_URL);
    const token = StorageService.getItem('session-token');
    const tokens: models.AuthResponse | null = token ? JSON.parse(token) : null;

    const { data } = await instance.post('/receivables', params, {
      headers: {
        Authorization: `Bearer ${tokens?.accessToken}`,
      },
    });

    return data;
  },

  getReceivableLink: async (params: PaymentLink) => {
    const instance = getInstance();
    const { data } = await instance.post('/receivables/partial', params);

    return data;
  },

  getExportedResponseCnab: async (id: string, isEmail: boolean) => {
    const instance = getInstance();
    const { data } = await instance.put(`/receivable-group/return-file/${id}`, {
      isEmail,
    });

    return data;
  },

  getNewReceivable: async (params: models.NewReceivableParams) => {
    const instance = getInstance();
    const { data } = await instance.get(
      '/receivables/detailed-movement-statement',
      { params }
    );

    return data;
  },

  postSendReceivableByEmail: async (billetUrl: string, email: string) => {
    const instance = getCleanInstance(API_URL);
    const token = StorageService.getItem('session-token');
    const tokens: models.AuthResponse | null = token ? JSON.parse(token) : null;

    const { data } = await instance.post(
      '/receivables/email-billing-billet',
      {
        email,
        billetUrl,
      },
      {
        headers: {
          Authorization: `Bearer ${tokens?.accessToken}`,
        },
      }
    );

    return data;
  },
};

export default ReceivableApi;
